// Design frames: hifi 09 (H9.1), wireframes 21 (21.1 to 21.8).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Card,
  Checkbox,
  Chip,
  DaysLeftBadge,
  List,
  ListItem,
  Notice,
  SelectField,
  Skeleton,
  StateScreen,
  Tabs,
  iconCall,
  useSnackbar,
} from '@sauda/ui/kit';
import { Screen, useChrome } from '../app/chrome.js';
import { useWindowClass } from '../app/media.js';
import { StatePanes } from '../app/panes.js';
import { LinkButton } from '../app/LinkButton.js';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { formatRupees } from '../lib/money.js';
import {
  RENEWAL_STATUSES,
  callable,
  RENEWAL_TABS,
  firstTab,
  renewalHero,
  type RenewalRow,
  type RenewalTab,
} from '../lib/renewals.js';
import { whatsappLink } from '../lib/share-text.js';
import { formatCalendarDate } from '../lib/time.js';
import { OldTenancySheet } from './OldTenancySheet.js';

/**
 * The renewal radar: the money expected in the next 60 days as the hero, a tab for each group of
 * days left, and a sheet per renewal for its outcome, starting the renewal deal and reminders.
 */
export function RenewalsScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const windowClass = useWindowClass();
  const cache = useQueryClient();
  const navigate = useNavigate();
  const snack = useSnackbar();
  const [tab, setTab] = useState<RenewalTab | null>(null);
  const [branch, setBranch] = useState('');
  const [agent, setAgent] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [addingOld, setAddingOld] = useState(false);
  const [attesting, setAttesting] = useState(false);
  const [consentLink, setConsentLink] = useState<string | null>(null);
  const [attested, setAttested] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['renewals', auth.agencyId],
    queryFn: () => api<RenewalRow[]>('/renewals'),
  });
  const all = query.data ?? [];
  const rows = all.filter(
    (r) =>
      (branch === '' || r.branchId === branch) && (agent === '' || r.assigneeMemberId === agent),
  );
  const shownTab = tab ?? firstTab(rows);
  // A renewal that has gone (its filter changed) closes its sheet.
  const open = rows.find((r) => r.id === openId) ?? null;
  useEffect(() => {
    if (openId !== null && open === null && query.isSuccess) setOpenId(null);
  }, [openId, open, query.isSuccess]);

  const refresh = () => cache.invalidateQueries({ queryKey: ['renewals'] });
  const update = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api(`/renewals/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: () => setError(t('renewals.failed')),
  });
  const start = useMutation({
    mutationFn: (id: string) =>
      api<{ id: string }>(`/renewals/${id}/start-deal`, { method: 'POST', body: {} }),
    onSuccess: async (deal) => {
      await refresh();
      void navigate(`/deals/${deal.id}`);
    },
    onError: () => setError(t('renewals.failed')),
  });
  const remind = useMutation({
    mutationFn: (id: string) =>
      api<{
        queued: number;
        alreadySent: boolean;
        ownerNeedsConsent: boolean;
        ownerConsentLink: string | null;
      }>(`/renewals/${id}/remind`, {
        method: 'POST',
        body: { attested: true },
      }),
    onSuccess: async (result) => {
      setAttesting(false);
      setAttested(false);
      setError(null);
      setConsentLink(result.ownerNeedsConsent ? result.ownerConsentLink : null);
      snack({
        text: t(
          result.alreadySent
            ? 'renewals.sentToday'
            : result.ownerNeedsConsent
              ? 'renewals.reminderSentTenant'
              : 'renewals.reminderSent',
        ),
      });
      await refresh();
    },
    onError: () => setError(t('renewals.failed')),
  });

  const branches = [...new Map(all.map((r) => [r.branchId, r.branchName]))];
  const agents = [
    ...new Map(
      all
        .filter((r) => r.assigneeMemberId !== null)
        .map((r) => [
          r.assigneeMemberId as string,
          r.assigneeName ?? (r.assigneeMemberId as string),
        ]),
    ),
  ];
  const hero = renewalHero(rows);
  const shown = rows.filter((r) => r.bucket === shownTab);
  const tabLabel: Record<RenewalTab, string> = {
    overdue: t('renewals.overdue'),
    d0_15: t('renewals.tab0to15'),
    d16_30: t('renewals.tab16to30'),
    d31_60: t('renewals.tab31to60'),
  };
  const readOnly = chrome.readOnly;
  const close = () => {
    setOpenId(null);
    setAttesting(false);
    setAttested(false);
  };

  const detailBody =
    open === null ? null : (
      <div className="form-stack">
        <Card hero className="k-card--strong">
          <p className="k-t-label-sm">{t('renewals.agreementEnds')}</p>
          <p className="k-t-display-sm">{formatCalendarDate(open.dueOn)}</p>
          <p className="k-t-body">
            {open.daysLeft < 0
              ? t('renewals.overdueDays', { count: -open.daysLeft })
              : t('renewals.inDays', { count: open.daysLeft })}
            {open.expectedBrokeragePaise === null
              ? ''
              : ` · ${t('renewals.expected', { amount: formatRupees(open.expectedBrokeragePaise) })}`}
          </p>
        </Card>
        <p className="k-t-body">
          {open.clientName}
          {open.assigneeName === null ? '' : ` · ${open.assigneeName}`}
        </p>
        {callable(open.tenantPhone) === null && callable(open.ownerPhone) === null ? null : (
          <div className="chips-row">
            {callable(open.tenantPhone) === null ? null : (
              <LinkButton
                variant="tonal"
                icon={iconCall}
                href={`tel:${callable(open.tenantPhone) ?? ''}`}
              >
                {t('renewals.callTenant')}
              </LinkButton>
            )}
            {callable(open.ownerPhone) === null ? null : (
              <LinkButton
                variant="tonal"
                icon={iconCall}
                href={`tel:${callable(open.ownerPhone) ?? ''}`}
              >
                {t('renewals.callOwner')}
              </LinkButton>
            )}
          </div>
        )}
        <div role="group" aria-label={t('renewals.status')} className="form-group">
          <p className="k-t-label">{t('renewals.outcome')}</p>
          <div className="chips-row">
            {RENEWAL_STATUSES.map((status) => (
              <Chip
                key={status}
                selected={open.status === status}
                disabled={readOnly}
                onClick={() => update.mutate({ id: open.id, status })}
              >
                {t(`renewals.status.${status}`)}
              </Chip>
            ))}
          </div>
        </div>
        {open.nextDealId === null ? (
          <Button
            size="lg"
            loading={start.isPending}
            disabled={readOnly}
            onClick={() => start.mutate(open.id)}
          >
            {t('renewals.startDeal')}
          </Button>
        ) : (
          <LinkButton to={`/deals/${open.nextDealId}`}>{t('renewals.openDeal')}</LinkButton>
        )}
        {consentLink === null ? null : (
          <Notice tone="info" title={t('renewals.ownerConsentTitle')}>
            <a
              href={whatsappLink(
                t('renewals.ownerConsentText', { link: consentLink }),
                open.ownerPhone,
              )}
              target="_blank"
              rel="noreferrer"
            >
              {t('renewals.ownerConsentSend')}
            </a>
          </Notice>
        )}
        {open.partiesOptIn ? (
          <>
            <p className="k-t-body-sm k-muted">{t('renewals.partiesOn')}</p>
            <Button
              variant="tonal"
              loading={remind.isPending}
              disabled={readOnly}
              onClick={() => remind.mutate(open.id)}
            >
              {t('renewals.notifyParties')}
            </Button>
          </>
        ) : attesting ? (
          <div className="form-stack">
            <Checkbox
              label={t('renewals.attest')}
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
            />
            <Button
              variant="tonal"
              disabled={!attested}
              loading={remind.isPending}
              onClick={() => remind.mutate(open.id)}
            >
              {t('renewals.attestConfirm')}
            </Button>
          </div>
        ) : (
          <Button variant="tonal" disabled={readOnly} onClick={() => setAttesting(true)}>
            {t('renewals.notifyParties')}
          </Button>
        )}
      </div>
    );
  const twoPane = windowClass === 'expanded' || windowClass === 'large';
  const listScreen = (
    <Screen title={t('renewals.title')} large eyebrow={auth.current?.agencyName}>
      {readOnly || !auth.allowed('deals.run') ? null : (
        <div>
          <Button variant="tonal" onClick={() => setAddingOld(true)}>
            {t('oldTenancy.title')}
          </Button>
        </div>
      )}
      <OldTenancySheet open={addingOld} onClose={() => setAddingOld(false)} />
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      {query.isPending ? <Skeleton height={120} /> : null}
      {query.isError ? (
        <Notice
          tone="error"
          action={
            <Button variant="text" onClick={() => void query.refetch()}>
              {t('app.retry')}
            </Button>
          }
        >
          {t('renewals.loadFailed')}
        </Notice>
      ) : null}
      {query.isSuccess ? (
        <Card hero>
          <p className="k-t-label k-muted">{t('renewals.next60')}</p>
          <p className="k-t-display hero-number" data-testid="renewal-hero">
            {formatRupees(hero.paise)}
          </p>
          <p className="k-t-body">{t('renewals.heroLine', { count: hero.count })}</p>
        </Card>
      ) : null}
      {branches.length > 1 || agents.length > 1 ? (
        <div className="form-pair">
          {branches.length > 1 ? (
            <SelectField
              label={t('renewals.filterBranch')}
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              options={[
                { value: '', label: t('renewals.all') },
                ...branches.map(([id, name]) => ({ value: id, label: name })),
              ]}
            />
          ) : null}
          {agents.length > 1 ? (
            <SelectField
              label={t('renewals.filterAgent')}
              value={agent}
              onChange={(e) => setAgent(e.target.value)}
              options={[
                { value: '', label: t('renewals.all') },
                ...agents.map(([id, name]) => ({ value: id, label: name })),
              ]}
            />
          ) : null}
        </div>
      ) : null}
      {query.isSuccess ? (
        <Tabs
          label={t('renewals.groups')}
          value={shownTab}
          onChange={(key) => setTab(key as RenewalTab)}
          tabs={RENEWAL_TABS.map((key) => ({
            key,
            label: tabLabel[key],
            ...(key === 'overdue'
              ? { badge: rows.filter((r) => r.bucket === 'overdue').length }
              : {}),
          }))}
        />
      ) : null}
      {query.isSuccess && shown.length === 0 ? (
        <StateScreen kind="empty" compact title={t('renewals.none')} />
      ) : null}
      {shown.length === 0 ? null : (
        <List label={tabLabel[shownTab]}>
          {shown.map((row) => (
            <ListItem
              key={row.id}
              title={row.propertyTitle}
              leading={
                <DaysLeftBadge
                  days={row.daysLeft}
                  unit={row.daysLeft < 0 ? t('renewals.lateUnit') : t('renewals.daysUnit')}
                  label={
                    row.daysLeft < 0
                      ? t('renewals.overdueDays', { count: -row.daysLeft })
                      : t('renewals.daysLeft', { count: row.daysLeft })
                  }
                />
              }
              supporting={[
                row.clientName,
                row.expectedBrokeragePaise === null
                  ? null
                  : formatRupees(row.expectedBrokeragePaise),
                row.nextDealId === null ? null : t('renewals.dealStarted'),
                row.assigneeName,
              ]
                .filter(Boolean)
                .join(' · ')}
              selected={openId === row.id}
              onClick={() => setOpenId(row.id)}
              action={
                callable(row.tenantPhone) === null ? undefined : (
                  <LinkButton variant="tonal" href={`tel:${callable(row.tenantPhone) ?? ''}`}>
                    {t('today.call')}
                  </LinkButton>
                )
              }
            />
          ))}
        </List>
      )}
    </Screen>
  );
  if (twoPane) {
    return (
      <StatePanes
        list={listScreen}
        detail={open === null ? null : <Screen title={open.propertyTitle}>{detailBody}</Screen>}
        listLabel={t('renewals.title')}
        detailLabel={t('panes.detail')}
        emptyTitle={t('panes.pickRenewal')}
      />
    );
  }
  return (
    <>
      {listScreen}
      <BottomSheet
        open={open !== null}
        onClose={close}
        label={open?.propertyTitle ?? t('renewals.title')}
        title={open?.propertyTitle ?? ''}
        closeLabel={t('app.close')}
      >
        {detailBody}
      </BottomSheet>
    </>
  );
}
